import { useQuery } from '@tanstack/react-query'

import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export interface BusinessNavigationLink {
  label: string
  url: string
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

export const businessNavigationQueryKey = ['user-business-navigation'] as const

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('user'),
  portal: 'user',
})

export function normalizeBusinessNavigation(value: unknown): BusinessNavigationLink | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const config = value as Record<string, unknown>
  if (
    config.enabled !== true ||
    typeof config.label !== 'string' ||
    typeof config.url !== 'string'
  ) {
    return null
  }
  const label = config.label.trim()
  const url = config.url.trim()
  if (!label || Array.from(label).length > 20 || !url || url.length > 2048) {
    return null
  }

  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password) {
      return null
    }
  } catch {
    return null
  }

  return { label, url }
}

export async function getBusinessNavigationLink() {
  const response = await apiClient.request<SuccessEnvelope<unknown>>(
    '/app/sys-settings/business-navigation',
  )
  return normalizeBusinessNavigation(response.data)
}

export function useBusinessNavigationLink() {
  const query = useQuery({
    enabled: Boolean(getStoredToken('user')),
    queryFn: getBusinessNavigationLink,
    queryKey: businessNavigationQueryKey,
    staleTime: 5 * 60 * 1000,
  })

  return query.data ?? null
}
